<template>
    <div class="flex flex-col">
        <div class="text-2xl text-center">
            <span>{{ date.format('YYYY-MM-DD') }}{{ schedules.length ? ':' : '' }}</span>
            <span v-if="groupedSchedules.length > 0" class="clickable">{{ getInitialCompanyAndLocationString() }}</span>
            <button @click="showInfoModalForFirstGroup()" class="p-2 border border-black rounded cursor-pointer"><i class="fa fa-info-circle"></i> Lokalinformation</button>
            <span class="flex items-start my-2">
                <button
                    v-if="!locked"
                    class="inline-flex items-center justify-center px-3 py-1 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-tlmblue2 hover:bg-tlmblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tlmblue2"
                    @click="showAddScheduleModal(groupedSchedules.length > 0 ? groupedSchedules[0][0] : null)"><i class="mr-1 fa fa-plus"></i>
                     {{ transEdit('add-schedule') }}
                </button>
            </span>
        </div>
        <div v-if="groupedSchedules.length > 0" class="flex flex-col pb-32 md:pb-0">
            <div v-for="(group, key, index) in groupedSchedules">
                <div v-if="key > 0" class="text-2xl text-center clickable" @click="showInfoModalForGroup(group)">
                    {{ getCompanyAndLocationString(group) }} <i class="fa fa-info-circle"></i>
                </div>
                <div v-for="schedule in group" class="flex mb-2">
                    <div class="flex flex-col justify-between w-full p-2 border rounded md:flex-row">
                        <div class="flex-grow">
                            <div class="text-xl">
                                <span v-if="schedule.booking" class="text-green-600"><i class="fa fa-check" title="Tiden bokad"></i></span>
                                <span v-else-if="schedule.preliminary_unbooking" class="text-warning"><i class="fa fa-times" title="Tiden preliminärt avbokad"></i></span>
                                <span v-else class="text-red-600"><i class="fa fa-times" title="Tiden ledig"></i></span>
                                {{ timespan(schedule.start, schedule.end) }}
                            </div>
                            <div>
                                <span v-if="bookingForSchedule(schedule)">{{ bookingForSchedule(schedule).treatment.name }}</span>
                                <span v-else-if="schedule.treatments.length > 1" :title="getTreatmentsString(schedule)">{{ schedule.treatments.length }} behandlingar</span>
                                <span v-else>{{ schedule.treatments[0] ? schedule.treatments[0].name : '-' }}</span>
                            </div>
                        </div>
                        <div :class="bookingForSchedule(schedule) && !bookingForSchedule(schedule).user ? 'flex items-center text-xl' : ''"
                             v-if="bookingForSchedule(schedule)">
                            <template v-if="bookingForSchedule(schedule).user">
                                <div class="text-xl">{{ bookingForSchedule(schedule).user.name }}</div>
                                <div>
                                    <a :href="'tel:'+bookingForSchedule(schedule).user.role.mobile">{{bookingForSchedule(schedule).user.role.mobile }}</a>
                                </div>
                                <div>
                                    <a :href="'mailto:'+bookingForSchedule(schedule).user.email">{{bookingForSchedule(schedule).user.email }}</a>
                                </div>
                            </template>
                            <template v-else-if="bookingForSchedule(schedule).customer_name">
                                <div class="text-xl">{{ bookingForSchedule(schedule).customer_name }}</div>
                            </template>
                        </div>
                        <div v-else></div>
                        <template v-if="! schedule.lock_id && !locked">
                            <div v-if="bookingForSchedule(schedule) && bookingForSchedule(schedule).user" class="flex flex-col justify-around mt-2 ml-4 md:mt-0">
                                <button v-if="!bookingForSchedule(schedule).customer_missing" class="inline-flex justify-center px-3 py-1 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-tlmblue2 hover:bg-tlmblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tlmblue2" @click="showCustomerAbsentModal(schedule)">
                                    <i class="fa fa-user" style="margin-right: 2px; padding-top:2px;"></i>
                                    {{ transEdit('present') }}
                                </button>
                                <button v-else class="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-red-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400" @click="showCustomerPresentModal(schedule)">
                                    <i class="fa fa-user-times" style="margin-right: 2px; padding-top:2px;"></i>
                                    {{ transEdit('absent') }}
                                </button>
                                <button class="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tlmblue2" @click="showUnbookModal(schedule)">
                                    {{ transEdit('unbook') }}
                                </button>
                            </div>
                            <div v-else-if="! bookingForSchedule(schedule)" class="flex flex-col justify-around mt-2 ml-4 md:mt-0" style="width: 104px;">
                                <button @click="showDeleteScheduleModal(schedule)" class="inline-flex justify-center px-3 py-1 mb-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm">
                                    <i class="fa fa-trash" style="margin-right: 2px; padding-top:2px;"></i>
                                    {{ transEdit('remove') }}
                                </button>
                                <button v-if="scheduleCompanyCanLogIn(schedule)" @click="showBookModal(schedule)" class="inline-flex justify-center px-3 py-1 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-tlmblue2 hover:bg-tlmblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tlmblue2">
                                    <i class="fa fa-plus" style="margin-right: 2px; padding-top:2px;"></i>
                                    {{ transEdit('book') }}
                                </button>
                            </div>
                            <div v-else class="ml-4">
                                <button @click="showUnbookModal(schedule)" class="inline-flex justify-center px-3 py-1 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm">
                                    <i class="fa fa-trash" style="margin-right: 2px; padding-top:2px;"></i>
                                    {{ transEdit('remove-post-registration') }}
                                </button>
                            </div>
                        </template>
                        <template v-else>
                            <div class="flex items-center ml-4 text-xl">
                                <button class="inline-flex justify-center px-3 py-1 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" disabled>
                                    <i class="fa fa-lock" style="margin-right: 2px; padding-top:2px;"></i>
                                    {{ transEdit('schedule-is-locked') }}
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="text-black align-self-center tex-xl">{{ transEdit('therapist-no-schedules') }}</div>
    </div>
</template>

<script>
import TherapistDashModalShowLocationInfo from "./TherapistDashModalShowLocationInfo";
import TherapistDashBookModal from "./TherapistDashBookModal";

export default {
    components: {TherapistDashModalShowLocationInfo},
    props: [
        'date',
        'route_bookings',
        'show_daily',
        'do_refresh',
        'locked'
    ],
    data: function () {
        return {
            schedules: [],
            groupedSchedules: []
        };
    },
    name: "therapist-dash-schedule-table",
    watch: {
        date: function (newDate, oldDate) {
            this.fetchNewBookingsData(new Date(newDate));
        },
        show_daily: function (showingWeekly) {
            if (!showingWeekly) {
                this.fetchNewBookingsData(this.date);
            }
        }
    },
    methods: {
        bookingForSchedule(schedule) {
            return schedule.booking ? schedule.booking : schedule.preliminary_unbooking;
        },
        scheduleCompanyCanLogIn(schedule) {
            return schedule.location && schedule.location.primary_company && schedule.location.primary_company.customers_can_log_in;
        },
        showAddScheduleModal(schedule) {
            this.$eventHub.$emit('therapist-add-schedule', this.date.format('YYYY-MM-DD'), schedule);
        },
        getInitialCompanyAndLocationString() {
            let group = this.groupedSchedules[Object.keys(this.groupedSchedules)[0]];
            return this.getCompanyAndLocationString(group);
        },
        getCompanyAndLocationString(scheduleGroup) {
            let firstSchedule = scheduleGroup[Object.keys(scheduleGroup)[0]];
            return firstSchedule.location.address + ", " + firstSchedule.location.primary_company.name + ", " + firstSchedule.location.city.name;
        },
        showInfoModalForFirstGroup() {
            if (Object.keys(this.groupedSchedules).length > 0) {
                this.showInfoModalForGroup(this.groupedSchedules[Object.keys(this.groupedSchedules)[0]]);
            }
        },
        showInfoModalForGroup(group) {
            this.showInfoModal(group[0].location);
        },
        showInfoModal(location) {
            this.$modal.show(TherapistDashModalShowLocationInfo, {
                location: location
            }, {
                'width': '100%',
                'max-width': 800,
                'adaptive': true,
                'clickToClose': true,
                height: 'auto',
            });
        },
        refresh() {
            this.fetchNewBookingsData(new Date(this.date));
        },
        getTreatmentsString(schedule) {
            let treatmentsString = "";

            schedule.treatments.forEach(function (element, index) {
                treatmentsString += element.name;
                if (index < schedule.treatments.length - 1) {
                    treatmentsString += ', ';
                }
            });

            return treatmentsString;
        },
        showManualBookingModal: function (schedule) {
            let manualBookingModal = $("#manual-booking-modal");
            let manualBookingModalScheduleID = $("#manual-booking-modal-schedule-id");
            let manualBookingModalDate = $("#manual-booking-modal-date");
            let manualBookingModalTime = $("#manual-booking-modal-time");
            let manualBookingUserSearch = $("#user-autocomplete");
            let manualBookingUserID = $("#user-id");
            let manualBookingSelectTreatment = $("#manual-booking-modal-treatments");
            let manualBookingAnonymousBooking = $("#manual-booking-modal-anonymous-booking");
            let companyToUse = schedule.location.primary_company ? schedule.location.primary_company : schedule.location.companies[0];
            let anonBookingsAllowed = companyToUse.allow_anonymous_bookings;

            manualBookingModalScheduleID.val(schedule.id);

            if (anonBookingsAllowed) {
                manualBookingAnonymousBooking.prop("disabled", false);
                manualBookingAnonymousBooking.closest('div').show();
            } else {
                manualBookingAnonymousBooking.prop("disabled", true);
                manualBookingAnonymousBooking.closest('div').hide();
            }

            let treatmentArray = schedule.treatments;
            manualBookingSelectTreatment.empty();
            treatmentArray.forEach(function (element) {
                manualBookingSelectTreatment.append($("<option></option>")
                    .attr("value", element.id)
                    .text(element.name));
            });

            let scheduleStart = new Date(schedule.start);
            let scheduleStartDate = scheduleStart.getYMD();
            let scheduleStartTime = scheduleStart.getHrsMins();
            manualBookingUserSearch.val(null);
            manualBookingUserID.val(null);

            manualBookingModalScheduleID.val(schedule.id);
            manualBookingModalDate.text(scheduleStartDate);
            manualBookingModalTime.text(scheduleStartTime);

            $("#manual-booking-modal-missing-user").hide();
            manualBookingModal.modal("show");
        },
        showBookModal(schedule) {
            this.$modal.show(TherapistDashBookModal, {
                'schedule': schedule,
            }, {
                height: '600px',
            },{
            'before-close': this.refresh
            });
        },
        showCustomerPresentModal: function (schedule) {
            let info = {};

            let startDate = new Date(schedule.start);
            let endDate = new Date(schedule.end);
            let bookingId = bookingForSchedule(schedule).id;
            let user = bookingForSchedule(schedule).user;
            let customerName = "";

            if (user.name) {
                customerName = user.name;
            }

            let dateString = startDate.getYMD();
            let timeString = startDate.getHrsMins() + " - " + endDate.getHrsMins();

            info.date = dateString;
            info.time = timeString;
            info.customerName = customerName;
            info.bookingId = bookingId;

            this.$eventHub.$emit('therapist-customer-present', info)
        },
        showCustomerAbsentModal: function (schedule) {
            let info = {};
            let startDate = new Date(schedule.start);
            let endDate = new Date(schedule.end);
            let bookingId = bookingForSchedule(schedule).id;
            let user = bookingForSchedule(schedule).user;
            let customerName = "";

            if (user.name) {
                customerName = user.name;
            }

            let dateString = startDate.getYMD();
            let timeString = startDate.getHrsMins() + " - " + endDate.getHrsMins();

            info.date = dateString;
            info.time = timeString;
            info.customerName = customerName;
            info.bookingId = bookingId;
            info.comment = '';

            this.$eventHub.$emit('therapist-customer-absent', info)
        },
        showDeleteScheduleModal: function (schedule) {
            let info = {};

            let startDate = new Date(schedule.start);
            let endDate = new Date(schedule.end);
            let scheduleId = schedule.id;

            let dateString = startDate.getYMD();
            let timeString = startDate.getHrsMins() + " - " + endDate.getHrsMins();

            info.date = dateString;
            info.time = timeString;
            info.scheduleId = scheduleId;

            this.$eventHub.$emit('therapist-delete-booking', info)
        },
        showUnbookModal: function (schedule) {
            let info = {};
            let startDate = new Date(schedule.start);
            let endDate = new Date(schedule.end);
            let scheduleId = schedule.id;

            let dateString = startDate.getYMD();
            let timeString = startDate.getHrsMins() + " - " + endDate.getHrsMins();

            info.date = dateString;
            info.time = timeString;
            info.scheduleId = scheduleId;

            this.$eventHub.$emit('therapist-customer-unbook', info)
        },
        prettyBookingData: function (booking) {
            let bookingDataHTML = "";
            let user = booking.user;

            if (user) {
                bookingDataHTML += "<b>" + user.name + "</b>";
            }

            return bookingDataHTML;

        },
        timespan: function (start, end) {
            let startDate = new Date(start);
            let endDate = new Date(end);

            let timespanString = startDate.getHrsMins() + " - " + endDate.getHrsMins();
            return timespanString;
        },
        humanReadableTimespan: function (timespan) {
            let minsRaw = timespan / 60;

            let hrs = Math.floor(minsRaw / 60);
            let mins = minsRaw % 60;

            let timeString;

            if (mins > 0) {
                if (mins === 1) {
                    timeString = mins + " minut";
                } else {
                    timeString = mins + " minuter";
                }
            } else {
                timeString = "";
            }

            if (hrs > 0) {
                if (hrs === 1) {
                    timeString = hrs + " timme, " + timeString;
                } else {
                    timeString = hrs + " timmar, " + timeString;
                }
            }

            return timeString;
        },
        getTreatmentTypesString: function (treatmentsArray) {
            let length = treatmentsArray.length;

            if (!length) {
                return "Wrong type on treatmentsArray";
            }

            if (!(length > 0)) {
                return "TREATMENTS ARRAY EMPTY";
            }

            if (length === 1) {
                return treatmentsArray[0].name;
            }

            let treatmentsString = treatmentsArray.pop().name;

            treatmentsArray.forEach(function (treatment) {
                treatmentsString += ", " + treatment.name;
            });

            return treatmentsString;
        },
        fetchNewBookingsData: function (date = new Date()) {
            date = date.getYMD();
            let self = this;

            axios.get(this.$portalUrl(self.route_bookings), {
                params: {
                    date: date,
                }
            })
                .then(function (response) {
                    self.groupedSchedules = response.data.grouped_schedules;
                    self.schedules = response.data.schedules;
                    self.$emit('new-schedules', response.data);
                })
                .catch(function (error) {
                });
        }
    },
    mounted() {
        this.fetchNewBookingsData(new Date(this.date));
    }
}
</script>

<style scoped>
.border-2 {
    border: 2px solid;
}

.bg-booked {
    background-color: rgba(250, 229, 229, 0.6);
}
</style>
